import React, { useCallback, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import CheckboxTree from 'react-checkbox-tree';
import { messages } from './messages';
import { useIntl } from 'react-intl';
import './nested-checkbox-tree.less';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons/faChevronDown';
import { faChevronUp } from '@fortawesome/pro-solid-svg-icons/faChevronUp';
import { faCheckSquare } from '@fortawesome/pro-solid-svg-icons/faCheckSquare';
import { faSquare } from '@fortawesome/pro-solid-svg-icons/faSquare';
import { faMinusSquare } from '@fortawesome/pro-solid-svg-icons/faMinusSquare';
import { expandNodesToLevel, getCheckedNodes } from './utils';
import { NestedCheckboxTreeNodePropType } from './nested-checkbox-tree-node-prop-type';

export const NestedCheckboxTree = ({ data, addRootNode = false, checkedCallback, title = '', disabled }) => {
  const intl = useIntl();

  const nodes = useMemo(() => {
    if (addRootNode && data.length) {
      return [
        {
          value: 0,
          label: intl.formatMessage(messages.rootNodeLabel),
          children: data,
        },
      ];
    }

    return data;
  }, [addRootNode, data, intl]);

  const [expanded, setExpanded] = useState(expandNodesToLevel(nodes, Infinity));
  const [checked, setChecked] = useState(getCheckedNodes(nodes));

  const onCheck = useCallback(
    (newChecked) => {
      setChecked(newChecked);
      checkedCallback(newChecked);
    },
    [checkedCallback],
  );

  return (
    <div className='nested-checkbox-tree'>
      <p className='title'>{title}</p>
      <CheckboxTree
        checked={checked}
        disabled={disabled}
        expanded={expanded}
        icons={{
          expandClose: <FontAwesomeIcon className='rct-icon-expand-close' icon={faChevronDown} />,
          expandOpen: <FontAwesomeIcon className='rct-icon-expand-open' icon={faChevronUp} />,
          check: <FontAwesomeIcon className='rct-icon-check' icon={faCheckSquare} />,
          uncheck: <FontAwesomeIcon className='rct-icon-check' icon={faSquare} />,
          halfCheck: <FontAwesomeIcon className='rct-icon-check' icon={faMinusSquare} />,
        }}
        nodes={nodes}
        onCheck={(newChecked) => onCheck(newChecked)}
        onExpand={(newExpanded) => setExpanded(newExpanded)}
      />
    </div>
  );
};

NestedCheckboxTree.propTypes = {
  data: PropTypes.arrayOf(NestedCheckboxTreeNodePropType).isRequired,
  disabled: PropTypes.bool.isRequired,
  addRootNode: PropTypes.bool,
  checkedCallback: PropTypes.func.isRequired,
  title: PropTypes.string,
};
